export const STRATEGY_RD_PATH = "/strategy-rd";

/** True for `/strategy-rd` and nested Lab paths. */
export function isStrategyRdPathname(pathname: string): boolean {
  const path = pathname.split("?")[0]?.replace(/\/+$/, "") || "";
  return path === STRATEGY_RD_PATH || path.startsWith(`${STRATEGY_RD_PATH}/`);
}

export const STRATEGY_RD_DISABLED_SLUG = "strategy_rd.disabled";

/** Public Tessera API base for Strategy R&D v1 (no v0). */
export const STRATEGY_RD_API_BASE = "/strategy-rd/v1";

export const STRATEGY_RD_CANDLE_TYPES = [
  "1m",
  "5m",
  "15m",
  "30m",
  "1h",
  "4h",
  "1d",
] as const;

export type StrategyRdCandleType = (typeof STRATEGY_RD_CANDLE_TYPES)[number];

/** Setup / pattern id — Concepts v1 library (open string; catalog is source of truth). */
export type StrategyRdPatternId = string;

export type StrategyRdDirection = "long" | "short" | "neutral";

export type StrategyRdScanStatus = "ready" | "backfilling";

export type StrategyRdConceptRole = "setup" | "confluence" | "confirmation" | "exit_helper";

export type StrategyRdConceptWave = "A" | "B" | string;

/** Legacy singleton ids; catalog `sessions` is the source of truth (includes overlaps). */
export type StrategyRdSessionId = "london" | "new_york" | "tokyo" | "sydney" | string;

export type StrategyRdSessionKind = "singleton" | "overlap" | string;

export interface StrategyRdSessionOption {
  id: string;
  name: string;
  kind: StrategyRdSessionKind;
  start_hour?: number | null;
  end_hour?: number | null;
  parts?: string[];
  ui_sort_order?: number | null;
}

export interface StrategyRdSessionsPayload {
  version?: string | null;
  timezone?: string | null;
  sessions: StrategyRdSessionOption[];
  singleton_ids?: string[];
  all_sentinel?: string;
  semantics?: Record<string, unknown> | null;
}

export interface StrategyRdPreset {
  id: string;
  name: string;
  description: string;
}

/** How the catalog wants a side chosen for this concept. */
export type StrategyRdDirectionMode =
  | "implied_long"
  | "implied_short"
  | "side_required"
  | "none"
  | string;

/** Per-slot trade side on nested composer (aliases like bull/bear normalized on BE). */
export type StrategyRdSlotSide = "long" | "short" | "both" | "invert";

/** Pattern polarity for directional setups (bullish/bearish FVG, …). Independent of trade side. */
export type StrategyRdPatternPolarity = "bull" | "bear" | "both";

export type StrategyRdConceptSettingValueKind =
  | "enum"
  | "string"
  | "number"
  | "boolean"
  | string;

export interface StrategyRdConceptSettingEnumValue {
  /** `null` = "Any" (no limit) — see `STRATEGY_RD_SETTING_ANY`. */
  value: string | number | null;
  label: string;
  /** Extra detail, e.g. a Killzone session's UTC hours. */
  description?: string | null;
}

/** Catalog setting schema — drive composer controls + request slots. */
export interface StrategyRdConceptSetting {
  key: string;
  label?: string | null;
  value_kind: StrategyRdConceptSettingValueKind;
  required?: boolean;
  default?: string | number | boolean | null;
  /** When true, value goes under slot.params (not top-level side/polarity/candle_type). */
  param?: boolean;
  enum_values?: StrategyRdConceptSettingEnumValue[] | null;
  /** Show this setting only when other keys match listed values. */
  visible_when?: Record<string, string | string[]> | null;
}

export interface StrategyRdComposerSetupSlot {
  id: string;
  /** Trade branch / entry bias — independent of pattern/level polarity. */
  side?: StrategyRdSlotSide | string | null;
  /**
   * Pattern (bull/bear/both) or Level (high/low/both) from catalog settings.
   * Independent of trade side — do not couple High→Long in the UI.
   */
  polarity?: StrategyRdPatternPolarity | string | null;
  /**
   * Alias of `polarity` for Level setups (high|low|both) — BE accepts either; FE sends both.
   */
  level?: string | null;
  /**
   * When Pattern/Level/Pool = Both: trade side per polarity branch.
   * e.g. { bull: "long", bear: "short" } or crossed { high: "short", low: "long" }.
   * Wire also mirrors as `polarity_sides` for BE.
   */
  side_by_polarity?: {
    bull?: StrategyRdSlotSide | string | null;
    bear?: StrategyRdSlotSide | string | null;
    /** When Level = Both (ORB / PDH / developing H-L). */
    high?: StrategyRdSlotSide | string | null;
    low?: StrategyRdSlotSide | string | null;
    /** When Pool = Both (liquidity sweep). */
    buyside?: StrategyRdSlotSide | string | null;
    sellside?: StrategyRdSlotSide | string | null;
  } | null;
  /**
   * BE alias of `side_by_polarity` when polarity = both.
   * Prefer this (or `side: "invert"`) so Logic path is not treated as neutral.
   */
  polarity_sides?: {
    bull?: StrategyRdSlotSide | string | null;
    bear?: StrategyRdSlotSide | string | null;
    high?: StrategyRdSlotSide | string | null;
    low?: StrategyRdSlotSide | string | null;
    buyside?: StrategyRdSlotSide | string | null;
    sellside?: StrategyRdSlotSide | string | null;
  } | null;
  /** True when High→Short · Low→Long (or Bull→Short · Bear→Long). */
  invert?: boolean | null;
  /** Must match Step-1 candle_type when sent; omit or equal setup TF. */
  candle_type?: string | null;
  /** Param-flagged setting values (e.g. length, label, MA location). */
  params?: Record<string, unknown> | null;
  /** Echoed by BE after normalize (e.g. bull_engulf + bear_engulf when polarity=both). */
  resolved_ids?: string[] | null;
  /** Echoed by BE after normalize. */
  tf_locked?: boolean;
  /** Catalog display name stamped for FE tooltips / chips. */
  name?: string | null;
  /** Catalog description stamped for FE tooltips / chips. */
  description?: string | null;
}

export interface StrategyRdComposerConfluenceSlot {
  id: string;
  /** Which trade branch this filter applies to. */
  side?: StrategyRdSlotSide | string | null;
  /** Top-level polarity when catalog marks it non-param (rare); MA uses params.polarity. */
  polarity?: string | null;
  /**
   * When Location = Both (params.polarity): trade side per location.
   * e.g. { above: "long", below: "short" }.
   */
  side_by_polarity?: {
    bull?: StrategyRdSlotSide | string | null;
    bear?: StrategyRdSlotSide | string | null;
    above?: StrategyRdSlotSide | string | null;
    below?: StrategyRdSlotSide | string | null;
  } | null;
  candle_type?: string | null;
  /** Accepted aliases when reading; serialize as candle_type. */
  tf?: string | null;
  timeframe?: string | null;
  /** Param-flagged setting values (length, label, polarity above/below, …). */
  params?: Record<string, unknown> | null;
  /** @deprecated Prefer params — kept when reading older payloads. */
  settings?: Record<string, unknown> | null;
  /** Catalog display name stamped for FE tooltips / chips. */
  name?: string | null;
  /** Catalog description stamped for FE tooltips / chips. */
  description?: string | null;
  /** N of M: counts toward `optional_min` instead of having to pass (filter confluences only). */
  optional?: boolean | null;
}

/**
 * Entry → Max wait: hard limit from the setup bar's close (confluence forming, interaction
 * and fill). `bars` = bars of the setup TF; `none` = explicit No limit (no value).
 * Omitted = BE default of 24 setup-TF bars.
 */
export interface StrategyRdMaxWait {
  value?: number | null;
  unit: "hours" | "days" | "bars" | "none";
}

export interface StrategyRdComposerEntrySlot {
  mode: string;
  side?: StrategyRdSlotSide | string | null;
  candle_type?: string | null;
  /** Omit when the Max wait box is empty (= 24 setup-TF bars). `{ unit: "none" }` = No limit. */
  max_wait?: StrategyRdMaxWait | null;
}

/** BE human recipe narrative — display-ready; do not rebuild on FE. */
export interface StrategyRdLogicPath {
  /** “How we read your settings” — trade side vs level/pattern/pool, entry modes, direction-required cases. */
  notes: string[];
  /** Reading cue + branch If-lines. */
  summary: string[];
  /** Branch steps (level, trade side, cue, entry mode, path-marker note). */
  long: string[];
  short: string[];
  text: string;
}

/** Top-level preview/scan guidance when entry config cannot produce a path. */
export interface StrategyRdEntryGuidance {
  invalid_reason?: string | null;
  advice?: string | null;
  user_message?: string | null;
}

export interface StrategyRdInstrument {
  instrument_id: string;
  display_symbol: string;
  display_name?: string | null;
  asset_class?: string | null;
  market_data_broker?: string | null;
  market_data_broker_display?: string | null;
  md_source?: string | null;
  source_hidden?: boolean;
  from_overlay?: boolean;
}

export interface StrategyRdInstrumentsResponse {
  instruments: StrategyRdInstrument[];
  count: number;
  liquid_eligible: boolean;
}

export interface StrategyRdConceptBranchHint {
  long?: string | null;
  short?: string | null;
  label?: string | null;
}

export interface StrategyRdConcept {
  id: string;
  family: string;
  role: StrategyRdConceptRole | string;
  /** Dual-role rows (e.g. coil_range_contract) may list every slot they belong to. */
  roles?: string[] | null;
  wave: StrategyRdConceptWave;
  name: string;
  description?: string | null;
  direction_mode?: StrategyRdDirectionMode | null;
  /**
   * Catalog taxonomy (Wave C+): reversal | continuation | bilateral | context | …
   * Used for grouping / drawer hints — never invent on FE.
   */
  pattern_class?: string | null;
  /** Catalog taxonomy: single | double | triple | multi | state. */
  bar_span?: string | null;
  /** Prefer these agnostic ids in the picker; legacy concrete ids still accepted. */
  agnostic?: boolean;
  /**
   * Legacy concrete ids superseded by this family row.
   * Array form, or direction map e.g. `{ long: "stage_2", short: "stage_4" }`.
   */
  legacy_ids?: string[] | Record<string, string> | null;
  /** Catalog taxonomy kind (e.g. stage) — distinct from `family` when BE sends both. */
  family_kind?: string | null;
  /** When true, omit from Setup / Confluence pickers (still accepted if already selected). */
  picker_hidden?: boolean;
  /** Prefer this family id in the picker (legacy concretes stay requestable). */
  superseded_by?: string | null;
  /** Groups mirrored concretes (e.g. sma50 → above/below). */
  pair_group?: string | null;
  /** BE chip copy for how Both expands per branch — do not invent on FE. */
  branch_hint?: StrategyRdConceptBranchHint | null;
  volume_requires?: boolean;
  /** When true, Scan/preview require non-empty session_ids. */
  requires_session?: boolean;
  /** ORB / PDH / developing H-L — Level (high/low/both) + Trade side from catalog settings. */
  level_extremes?: boolean;
  /**
   * Location confluence — arm → interact → fill (FVG / OB / sweep / BOS / trendline).
   * Context chips omit Interaction UI.
   */
  location_interaction?: boolean;
  params?: Record<string, unknown> | null;
  /** Settings schema from BE — render as-is; do not invent FE controls. */
  settings?: StrategyRdConceptSetting[] | null;
}

export interface StrategyRdPack {
  id: string;
  wave: StrategyRdConceptWave;
  name: string;
  setup_id: string;
  confluences: string[];
  entry_mode: StrategyRdEntryMode | string;
  stop_offset_kind?: StrategyRdOffsetKind | string | null;
  description?: string | null;
  /** Pack default sessions (e.g. pack_session_orb → ["london"]). */
  session_ids?: string[] | null;
}

export interface StrategyRdCatalog {
  presets: StrategyRdPreset[];
  concepts: StrategyRdConcept[];
  packs: StrategyRdPack[];
  sessions?: StrategyRdSessionsPayload | null;
  doc?: string | null;
}

export interface StrategyRdChartParams {
  symbol: string;
  broker_account_code: string;
  candle_type: string;
  start_date: string;
  end_date: string;
  include_trades: boolean;
  fill_missing?: boolean;
  asset_class?: string;
  /** Scan lane pin — execution-candles must send these so the chart does not re-resolve. */
  market_data_broker?: string | null;
  md_source?: string | null;
  md_listing_key?: string | null;
}

export type StrategyRdMatchMeta = Record<string, unknown>;

export type StrategyRdSidePolicy = "both" | "long_only" | "short_only";

export type StrategyRdPathEventKind =
  | "setup"
  | "break"
  | "rejection"
  | "retest"
  | "location_armed"
  | "respect"
  | "reject"
  | "break_retest"
  | "entry"
  /** Rule entry: a limit / stop order rests at `price` from here until the fill or cancel. */
  | "order_placed"
  /** Rule entry: `cancel_if` held — the setup / resting order is dropped. */
  | "cancel";

export type StrategyRdPathStatus = "ready" | "incomplete";

export interface StrategyRdPathEvent {
  kind: StrategyRdPathEventKind;
  bar_time: string;
  direction?: StrategyRdDirection | null;
  price?: number | null;
  bar_index?: number | null;
  level_high?: number | null;
  level_low?: number | null;
  /** Location concept that armed / confirmed (zone or event). */
  concept_id?: string | null;
  /**
   * `event_confirm` = filter confirm (prior_liq_* / bos / choch) — never the Enter fill.
   * Zone fills use respect / reject / break_retest events instead.
   */
  role?: string | null;
  /** `order_placed`: `limit` | `stop`. */
  order?: string | null;
}

export interface StrategyRdMatchPath {
  status: StrategyRdPathStatus;
  entry_mode: string;
  side_policy: StrategyRdSidePolicy | string;
  /** TF of break/entry events (often finer than setup). */
  candle_type?: string | null;
  setup_bar_time: string | null;
  level_high?: number | null;
  level_low?: number | null;
  break_bar_time?: string | null;
  break_direction?: "long" | "short" | null;
  break_price?: number | null;
  /** Reject bar when entry.mode is rejection (pierce + close-back). */
  rejection_bar_time?: string | null;
  retest_bar_time?: string | null;
  /** Open of the entry bar (finer TF). Close-based fills print one bar later. */
  entry_bar_time?: string | null;
  /** Instant the fill prints. Close-based entries are one bar after entry_bar_time. */
  entry_time?: string | null;
  entry_price?: number | null;
  direction?: "long" | "short" | null;
  /** Which location concept drove fill when location interaction is on. */
  location_fill?: string | null;
  stop_price?: number | null;
  target_price?: number | null;
  stop_anchor?: string | null;
  target_mode?: string | null;
  /** Structural evidence for labels (price / period / swing bar_time). */
  stop_level?: Record<string, unknown> | null;
  target_level?: Record<string, unknown> | null;
  invalid_reason?: string | null;
  /** BE copy for incomplete paths — prefer over FE humanize of invalid_reason. */
  user_message?: string | null;
  /** Secondary BE advice when user_message is absent. */
  advice?: string | null;
  events: StrategyRdPathEvent[];
}

export interface StrategyRdMatch {
  pattern_id: string;
  setup_id?: string | null;
  symbol: string;
  candle_type: string;
  bar_time: string;
  direction: StrategyRdDirection;
  meta?: StrategyRdMatchMeta | null;
  /** Product B′ — Rules entry path (setup → break → retest → entry). */
  path?: StrategyRdMatchPath | null;
  md_source?: string | null;
  md_listing_key?: string | null;
  market_data_broker?: string | null;
  chart?: StrategyRdChartParams;
}

/** Full-window overlay marker from preview/scan `match_markers`. */
export interface StrategyRdMatchMarker {
  bar_time: string;
  bar_index?: number | null;
  direction: StrategyRdDirection;
  pattern_id?: string | null;
  setup_id?: string | null;
  session_id?: string | null;
  /** Echoed when the OHLC window has volume (overlay without re-indexing). */
  volume?: number | null;
  volume_prior?: number | null;
  vol_expand_ratio?: number | null;
  vol_expand_factor?: number | null;
  vol_expanded?: boolean | null;
  rvol?: number | null;
  rvol_avg?: number | null;
  rvol_lookback?: number | null;
  volume_available?: boolean | null;
  volume_status?: {
    available?: boolean | null;
    reason?: string | null;
  } | null;
}

export type StrategyRdSrScopeId = "day" | "week" | "month";

export type StrategyRdSrOpposing = {
  side: "support" | "resistance";
  high: number;
  low: number;
};

/** One prior-period high/low rail as used by `clear_of_opposing_sr`. */
export type StrategyRdSrRail = {
  scope: StrategyRdSrScopeId;
  high: number;
  low: number;
  high_label: string;
  low_label: string;
  valid_from: string;
  valid_to: string;
  period_start?: string | null;
  period_end?: string | null;
};

export type StrategyRdSrLevel = StrategyRdSrRail & {
  opposing?: StrategyRdSrOpposing | null;
};

/** `matches[].meta.sr` — levels evaluated at that setup bar. */
export type StrategyRdMatchSr = {
  scopes: StrategyRdSrScopeId[];
  timezone?: string | null;
  week_start?: string | null;
  status: string;
  levels: StrategyRdSrLevel[];
};

export interface StrategyRdOhlcStats {
  bars_scanned?: number;
  bars_expected?: number;
  coverage_ratio?: number;
  md_source?: string | null;
  md_listing_key?: string | null;
  market_data_broker?: string | null;
  window_start?: string | null;
  window_end?: string | null;
  candle_type?: string;
  lookback_days?: number;
  status?: string;
  strip_id?: string;
  setup_match_count?: number;
  after_filter_count?: number;
  match_count?: number;
  /** `"live"` on preview, `"cohort"` on scan — same detector. */
  detect_profile?: "live" | "cohort" | string;
  /**
   * Runtime volume flag from the OHLC window (not asset-class heuristics).
   * Prefer this over class alone — FX via Twelve Data is often false.
   */
  volume_available?: boolean | null;
  /** Structured volume status when BE sends `{ available, reason }`. */
  volume_status?: {
    available?: boolean | null;
    reason?: string | null;
  } | null;
  confluences?: {
    requested?: string[] | null;
    applied?: string[] | null;
    skipped?: Array<string | { id?: string; reason?: string }> | null;
    notes?: Array<{ id?: string; soft_pass?: string }> | null;
    [key: string]: unknown;
  } | null;
  [key: string]: unknown;
}

export interface StrategyRdScanRequest {
  symbol: string;
  /** Page gets only the last N candles; the full scan stays server-side (`scan_job_id`). */
  chart_bars?: number;
  /** Optional: the Lab reads the central market-data lane (§11.4). */
  broker_account_code?: string | null;
  pattern_id?: string;
  /** Preferred Concepts setup id (may mirror pattern_id). */
  setup_id?: string;
  /** Nested preferred setup slot (id + side). */
  setup?: StrategyRdComposerSetupSlot;
  /** Legacy flat ids or nested confluence slots. */
  confluences?: string[] | StrategyRdComposerConfluenceSlot[];
  /** N of M: at least this many `optional` confluences must pass. */
  optional_min?: number;
  /** Nested entry (mode + side + optional candle_type). */
  entry?: StrategyRdComposerEntrySlot;
  /** Day/week/month S/R levels when `clear_of_opposing_sr` is on. Omit/empty = all three. */
  sr_scopes?: string[];
  /** Preferred multi-session (OR of windows). Prefer over singular session_id. */
  session_ids?: string[];
  /** @deprecated Prefer session_ids. */
  session_id?: StrategyRdSessionId | string | null;
  candle_type?: string;
  lookback_days?: number;
  /** A specific period (YYYY-MM-DD); without them the window ends now. */
  start_date?: string | null;
  end_date?: string | null;
  asset_class?: string | null;
  resolved_symbol?: string | null;
  /** Pass from selected instrument (omit when Databento source_hidden). */
  market_data_broker?: string | null;
  md_source?: string | null;
  /** Draft Rules for path preview (shorthand). */
  entry_mode?: StrategyRdEntryMode | string;
  side_policy?: StrategyRdSidePolicy | string;
  /** Full draft template (optional; overrides shorthand when present). */
  template?: StrategyRdRuleTemplate | null;
}

export interface StrategyRdPreviewConceptsRequest {
  symbol: string;
  /** Optional: the Lab reads the central market-data lane (§11.4). */
  broker_account_code?: string | null;
  setup_id?: string;
  pattern_id?: string;
  setup?: StrategyRdComposerSetupSlot;
  confluences?: string[] | StrategyRdComposerConfluenceSlot[];
  optional_min?: number;
  entry?: StrategyRdComposerEntrySlot;
  /** Day/week/month S/R levels when `clear_of_opposing_sr` is on. Omit/empty = all three. */
  sr_scopes?: string[];
  session_ids?: string[];
  session_id?: string | null;
  candle_type?: string;
  lookback_days?: number;
  /** A specific period (YYYY-MM-DD); without them the window ends now. */
  start_date?: string | null;
  end_date?: string | null;
  asset_class?: string | null;
  resolved_symbol?: string | null;
  market_data_broker?: string | null;
  md_source?: string | null;
  md_listing_key?: string | null;
  entry_mode?: StrategyRdEntryMode | string;
  side_policy?: StrategyRdSidePolicy | string;
  template?: StrategyRdRuleTemplate | null;
  /** Prefer warm OHLC for sync 200; omit for async 202 job. */
  candles?: StrategyRdOhlcBar[];
}

/**
 * Sync preview-concepts / scan-shaped payload (matches + optional candles).
 * Live composer preview uses up to 20 example matches; Scan is the full cohort.
 */
export type StrategyRdPreviewConceptsResponse = StrategyRdScanResponse;

export interface StrategyRdScanResponse {
  status: StrategyRdScanStatus;
  pattern_id: string;
  setup_id?: string | null;
  symbol: string;
  resolved_symbol?: string | null;
  candle_type: string;
  lookback_days: number;
  matches: StrategyRdMatch[];
  ohlc_stats: StrategyRdOhlcStats;
  md_source?: string | null;
  md_listing_key?: string | null;
  market_data_broker?: string | null;
  strip_id?: string;
  /** Optional strip progress URL (do not treat as job terminal). */
  poll_url?: string;
  /** Canonical job poll URL while backfilling — keep polling this job_id. */
  job_poll_url?: string;
  /** BE loading copy while status is backfilling. */
  user_message?: string;
  /** OHLC for the chart: the full window, or its last `chart_bars` when trimmed. */
  candles?: StrategyRdOhlcBar[];
  /** The scan's job: Apply / path preview read its full candles server-side. */
  scan_job_id?: string | null;
  /** Bars in the full window (more than `candles` when trimmed). */
  candles_total?: number | null;
  candles_trimmed?: boolean;
  /** First bar of the full window (older bars than the chart holds). */
  window_first_bar?: string | null;
  /** Optional full-window date range / MD pin (do not use as the main series). */
  chart?: StrategyRdChartParams | null;
  chart_hint?: StrategyRdChartParams | null;
  /** Overlay markers on the full-window chart. */
  match_markers?: StrategyRdMatchMarker[];
  session_ids?: string[];
  confluences?: string[];
  path_preview?: boolean;
  /** Echoed when `clear_of_opposing_sr` was applied; null/omitted when confluence off. */
  sr_scopes?: string[] | null;
  /** Full-window prior day/week/month rails used by the detector. */
  sr_rails?: StrategyRdSrRail[];
  /** Normalized composer echo from BE (optional). */
  composer?: Record<string, unknown> | null;
  /** Human recipe narrative — hide panel when absent. */
  logic_path?: StrategyRdLogicPath | null;
  /**
   * Top-level entry config guidance (e.g. level_missing) — prefer for banners
   * even when incomplete matches were previously filtered out.
   */
  entry_guidance?: StrategyRdEntryGuidance | null;
  multi_tf?: boolean;
  pine_multi_tf_supported?: boolean;
  /** Per-TF chart query params for match dive (optional). */
  chart_by_tf?: Record<string, StrategyRdChartParams | Record<string, unknown>> | null;
  /** Present on Preview ready when match_count == 0 (BE-owned copy). */
  empty_state?: StrategyRdPreviewEmptyState | null;
}

export interface StrategyRdPreviewEmptyStateAction {
  id: string;
  label: string;
  goal?: string | null;
  faq_id?: string | null;
}

export interface StrategyRdPreviewEmptyState {
  code: string;
  title: string;
  body_markdown: string;
  hints?: string[] | null;
  suggested_actions?: StrategyRdPreviewEmptyStateAction[] | null;
}

export interface StrategyRdPreviewPathRequest {
  matches: StrategyRdMatch[];
  candles: StrategyRdOhlcBar[];
  /** Instead of `candles`: the scan's server-side window. */
  scan_job_id?: string | null;
  entry_mode?: StrategyRdEntryMode | string;
  side_policy?: StrategyRdSidePolicy | string;
  template?: StrategyRdRuleTemplate | null;
}

export interface StrategyRdPreviewPathResponse {
  status: string;
  matches: StrategyRdMatch[];
  path_preview: boolean;
  template?: StrategyRdRuleTemplate | null;
}

export interface StrategyRdResolveRequest {
  symbol: string;
  /** Optional: the Lab reads the central market-data lane (§11.4). */
  broker_account_code?: string | null;
  asset_class?: string | null;
  resolved_symbol?: string | null;
  /** Pass from selected instrument (omit when Databento source_hidden). */
  market_data_broker?: string | null;
  md_source?: string | null;
}

export interface StrategyRdResolveResponse {
  status: "ready" | string;
  symbol: string;
  resolved_symbol: string;
  trade_symbol?: string | null;
  asset_class?: string | null;
  market_data_broker?: string | null;
  market_data_broker_display?: string | null;
  md_source?: string | null;
  md_listing_key?: string | null;
  chart_available?: boolean;
  market_data_sources?: unknown;
}

export interface StrategyRdOhlcBar {
  timestamp: string;
  open: number;
  high: number;
  low: number;
  close: number;
  /** Present when the series has usable volume (equities/crypto; often missing on FX). */
  volume?: number | null;
}

export type StrategyRdEntryMode =
  | "signal_close"
  | "signal_open"
  | "next_open"
  | "break_close"
  | "retest_then_close"
  | "rejection"
  | "limit_at_level"
  /** Custom entry (`template.entry.rule`) — set from Rules, not the composer picker. */
  | "rule";
export type StrategyRdStopAnchor =
  | "signal_extreme"
  | "signal_close"
  | "level_extreme"
  | "prior_day"
  | "prior_week"
  | "prior_month"
  | "swing"
  | "prior_day_low"
  | "prior_day_high"
  | "prior_week_low"
  | "prior_week_high"
  | "prior_month_low"
  | "prior_month_high"
  | "swing_low"
  | "swing_high"
  | string;

export type StrategyRdOffsetKind =
  | "none"
  | "beyond_bar"
  | "beyond_level"
  | "pct_price"
  | "atr_mult"
  | "pips"
  | "ticks"
  | "price";

export type StrategyRdTargetMode =
  | "r_multiple"
  | "atr_mult"
  | "pct_price"
  | "pips"
  | "ticks"
  | "prior_day"
  | "prior_week"
  | "prior_month"
  | "swing"
  | "prior_day_low"
  | "prior_day_high"
  | "prior_week_low"
  | "prior_week_high"
  | "prior_month_low"
  | "prior_month_high"
  | "swing_low"
  | "swing_high"
  | "level_extreme"
  | string;
/** `exit` = closed by the time stop. */
export type StrategyRdForwardResult =
  | "target"
  | "stop"
  | "open"
  | "skipped"
  | "liquidated"
  | "exit";

/** Stop and target on the same bar (`template.intrabar`). */
export type StrategyRdIntrabarMode = "bar_path" | "bar_magnifier" | "stop_first";

export interface StrategyRdRuleOffset {
  kind: StrategyRdOffsetKind;
  value: number;
}

/** Floor or cap on the entry-to-stop distance. Each bound has its own unit. */
export type StrategyRdStopDistanceKind = "pct_price" | "atr_mult" | "pips" | "ticks" | "price";

export interface StrategyRdStopDistanceBound {
  kind: StrategyRdStopDistanceKind;
  value: number;
}

/**
 * A rulelang node (price expression or condition) — JSON the BE checks, reads back and
 * exports. The FE builds / edits it but never evaluates it.
 */
export type StrategyRdRuleExpr = Record<string, unknown>;

/** Where a custom rule plugs in (`POST /rules/compile` `role`). */
export type StrategyRdRuleRole = "stop" | "target" | "entry" | "filter";

/** `template.entry.rule` (entry mode `rule`). */
export interface StrategyRdEntryRuleSpec {
  order: "market" | "limit" | "stop";
  /** Limit / stop orders. */
  price?: StrategyRdRuleExpr | null;
  /** Market: required. Limit / stop: when to place the order (absent = at the setup). */
  when?: StrategyRdRuleExpr | null;
  cancel_if?: StrategyRdRuleExpr | null;
}

/** `template.manage` — break-even, trailing stop, partials, time stop. */
export interface StrategyRdManageRule {
  /** `lock_r < after_r`. */
  break_even?: { after_r: number; lock_r: number } | null;
  trail?: { kind: "atr" | "swing"; mult: number; after_r?: number | null } | null;
  /** Up to 3, increasing `at_r`, total `close_pct` ≤ 95. */
  partials?: Array<{ at_r: number; close_pct: number }> | null;
  time_stop_bars?: number | null;
  /** Close any open trade at a clock time ("16:00" New York = the session end). */
  exit_at?: { time: string; tz: string } | null;
}

export interface StrategyRdRuleTemplate {
  entry: {
    mode: StrategyRdEntryMode;
    side_policy?: StrategyRdSidePolicy | string;
    /** Omit when the Max wait box is empty. */
    max_wait?: StrategyRdMaxWait | null;
    /** Mode `rule` only. */
    rule?: StrategyRdEntryRuleSpec | null;
    /** Any mode: checked once on the ready bar; false drops the setup (not a hit). */
    filter?: StrategyRdRuleExpr | null;
    /** Break / retest / rejection: fill at the confirming close (default) or the next candle's open. */
    fill?: "close" | "next_open";
  };
  stop: {
    anchor: StrategyRdStopAnchor;
    offset: StrategyRdRuleOffset;
    /** Omit when the Min stop box is empty. */
    min_distance?: StrategyRdStopDistanceBound | null;
    /** Omit when the Max stop box is empty. */
    max_distance?: StrategyRdStopDistanceBound | null;
    /** Custom stop — replaces anchor / offset (min / max distance still apply). */
    rule?: StrategyRdRuleExpr | null;
  };
  target: {
    mode: StrategyRdTargetMode;
    value: number;
    /** Custom target — replaces mode / value; may use `{r: x}`. */
    rule?: StrategyRdRuleExpr | null;
    /** Skip the trade when the target is nearer than this many R at the fill. */
    min_r?: number | null;
  };
  atr_length: number;
  pip_size?: number | null;
  tick_size?: number | null;
  /** Omitted = BE default `bar_path` (TradingView's bar path). */
  intrabar?: StrategyRdIntrabarMode | null;
  manage?: StrategyRdManageRule | null;
}

/** `POST /rules/vocabulary` — everything the rule editor's pickers offer. */
export interface StrategyRdRuleVocabularySlot {
  values: string[];
  concept_id?: string | null;
  /** e.g. "Near breaker block". */
  name?: string | null;
}

export interface StrategyRdRuleVocabulary {
  /** Built-in sources → values; confluence slot keys → `{values, concept_id, name}`. */
  sources: Record<string, string[] | StrategyRdRuleVocabularySlot>;
  /** Confluence slot keys that carry no price (true / false filters). */
  not_usable: string[];
  distances: Record<string, string>;
  operators: Record<string, string>;
  edges?: string | null;
  examples: Record<string, StrategyRdRuleExpr>;
  entry?: {
    notes?: string | null;
    conditions?: Record<string, string>;
    examples?: Record<string, StrategyRdRuleExpr>;
  } | null;
  filter?: { notes?: string | null; examples?: Record<string, StrategyRdRuleExpr> } | null;
  n_of_m?: string | null;
  manage?: Record<string, string> | null;
  account_limits?: string | null;
}

/** `POST /rules/compile`. */
export interface StrategyRdRuleCompileResult {
  ok: boolean;
  /** Plain sentences for the user — show as they are. */
  errors: string[];
  readback?: string | null;
  template_patch?: Record<string, unknown> | null;
}

export interface StrategyRdScanContext {
  symbol: string;
  /** Optional: the Lab reads the central market-data lane (§11.4). */
  broker_account_code?: string | null;
  pattern_id?: string | null;
  setup_id?: string | null;
  setup?: StrategyRdComposerSetupSlot | null;
  confluences?: string[] | StrategyRdComposerConfluenceSlot[] | null;
  optional_min?: number | null;
  entry?: StrategyRdComposerEntrySlot | null;
  /** The normalized composer the BE stored (slot objects with params / optional). */
  composer?: Record<string, unknown> | null;
  sr_scopes?: string[] | null;
  session_ids?: string[] | null;
  /** @deprecated Prefer session_ids. */
  session_id?: string | null;
  candle_type?: string | null;
  resolved_symbol?: string | null;
  asset_class?: string | null;
  lookback_days?: number | null;
  start_date?: string | null;
  end_date?: string | null;
  market_data_broker?: string | null;
  md_source?: string | null;
}

export interface StrategyRdHypothesis {
  id: string;
  name: string;
  created_at?: string | null;
  updated_at?: string | null;
  scan_context: StrategyRdScanContext;
  template: StrategyRdRuleTemplate;
  report_strategy_hint?: {
    report_strategy_id?: string | null;
    deep_link?: string | null;
  } | null;
  last_apply?: StrategyRdLastApply | null;
  last_robustness?: StrategyRdRobustness | null;
  /** Raw `last_markets` (parse with parseStrategyRdMarketsResult). */
  last_markets?: unknown;
  /** Versions: the backtest this one was made from, the family's first, its number. */
  parent_id?: string | null;
  root_id?: string | null;
  version?: number | null;
  created_by?: "user" | "tes" | string | null;
}

/** Headline stats of a backtest (`quant_eval.headline`). */
export interface StrategyRdHeadlineStats {
  closed_count?: number | null;
  win_rate?: number | null;
  expectancy_r?: number | null;
  sum_r?: number | null;
  profit_factor_r?: number | null;
  max_drawdown_r?: number | null;
  sum_pnl?: number | null;
  return_on_starting_equity_pct?: number | null;
  profit_factor?: number | null;
  max_drawdown_pct?: number | null;
}

/** Hold-out: the tuning period vs the last `holdout_pct` % (unseen while tuning). */
export interface StrategyRdHoldoutSplit {
  holdout_pct?: number | null;
  cut_time?: string | null;
  /** First and last setup times: the tuning period is first → cut, the unseen cut → last. */
  first_time?: string | null;
  last_time?: string | null;
  tuning: StrategyRdHeadlineStats;
  unseen: StrategyRdHeadlineStats;
  verdict: "held_up" | "weaker" | "failed" | "too_few_trades" | string;
}

/** First vs second half of the locked trade list (`overview.stability.halves`). */
export interface StrategyRdHalves {
  cut_time?: string | null;
  first: StrategyRdHeadlineStats;
  second: StrategyRdHeadlineStats;
  verdict: "both_worked" | "first_only" | "second_only" | "neither" | "too_few_trades" | string;
}

/** Would the edge survive without the best few trades (`overview.stability.concentration`). */
export interface StrategyRdConcentration {
  verdict: "diversified" | "concentrated" | "too_few_trades" | string;
  dropped?: number | null;
  best_share_of_sum_r?: number | null;
  expectancy_r?: number | null;
  expectancy_r_without_best?: number | null;
  closed_count?: number | null;
}

/** One calendar month of the locked list (`overview.stability.months`). */
export interface StrategyRdMonthBucket {
  month: string;
  closed_count?: number | null;
  expectancy_r?: number | null;
  sum_r?: number | null;
  sum_pnl?: number | null;
}

/** Apply `overview.stability` — halves, months, concentration on the same trades. */
export interface StrategyRdStability {
  halves?: StrategyRdHalves | null;
  concentration?: StrategyRdConcentration | null;
  months?: StrategyRdMonthBucket[] | null;
}

/** Robustness `cost_stress`: half/double the user's costs, or assumed 0.04% + 2 ticks. */
export interface StrategyRdCostStressVariant {
  label: "half_cost" | "double_cost" | "assumed_costs" | string;
  return_pct?: number | null;
  net_pnl?: number | null;
  trades?: number | null;
  win_rate?: number | null;
}

export interface StrategyRdCostStress {
  verdict: "ok" | "fragile_to_costs" | string;
  variants: StrategyRdCostStressVariant[];
}

/** Parameter search table (`overview.sweep` / result card `sweep`). */
export interface StrategyRdSweepTable {
  metric: string;
  holdout_pct?: number | null;
  combinations: Array<{
    params: Record<string, unknown>;
    stats: StrategyRdHeadlineStats;
    split?: StrategyRdHoldoutSplit | null;
  }>;
  best: Record<string, unknown>;
  best_verdict?: string | null;
}

/** `GET /hypotheses/{id}/versions` row. */
export interface StrategyRdHypothesisVersion {
  id: string;
  name: string;
  parent_id?: string | null;
  root_id?: string | null;
  version?: number | null;
  created_by?: "user" | "tes" | string | null;
  created_at?: string | null;
  updated_at?: string | null;
  stats?: StrategyRdHeadlineStats | null;
  split?: StrategyRdHoldoutSplit | null;
}

/** `GET /rules/blocks` — a saved rule (private to the user). */
export interface StrategyRdRuleBlock {
  id: string;
  name: string;
  note?: string | null;
  role: StrategyRdRuleRole | "manage" | string;
  rule: Record<string, unknown>;
  readback?: string | null;
  requires?: Array<{ key: string; concept_id?: string | null; kind?: string | null }>;
  created_at?: string | null;
  updated_at?: string | null;
}

/** `POST /rules/blocks/{id}/use`. */
export interface StrategyRdRuleBlockUse {
  ok: boolean;
  errors: string[];
  readback?: string | null;
  template_patch?: Record<string, unknown> | null;
  mapping?: Record<string, string>;
  /** Saved slot key → this recipe's candidate slot keys (pick one, then call again with `remap`). */
  choices: Record<string, string[]>;
}

export interface StrategyRdPercentiles {
  p5: number | null;
  median: number | null;
  p95: number | null;
}

export interface StrategyRdFragileTrade {
  entry_time: string;
  side: "long" | "short" | string;
  result?: string | null;
  pnl?: number | null;
  kept_pct?: number | null;
  within_1_bar_pct?: number | null;
}

/**
 * `hypothesis.last_robustness` / the robustness job result. While it runs only
 * status + job_id + applied_at are set; failed carries `error`.
 */
export interface StrategyRdRobustness {
  status: "queued" | "running" | "ready" | "failed" | "not_started" | string;
  job_id?: string | null;
  applied_at?: string | null;
  /** Queued for an older backtest — did no work. */
  superseded?: boolean;
  error?: { code?: string | null; message?: string | null } | null;
  reason?: string | null;
  verdict?: "stable" | "sensitive" | string | null;
  summary?: string | null;
  runs?: number | null;
  baseline?: {
    trades?: number | null;
    net_pnl?: number | null;
    return_pct?: number | null;
    win_rate?: number | null;
  } | null;
  return_pct?: StrategyRdPercentiles | null;
  trades?: StrategyRdPercentiles | null;
  win_rate?: StrategyRdPercentiles | null;
  trade_stability?: {
    /** Fraction 0–1. */
    median?: number | null;
    p5?: number | null;
    extra_trades_median?: number | null;
  } | null;
  fragile_trades?: StrategyRdFragileTrade[];
  fragile_count?: number | null;
  calibration?: { source?: string | null; note?: string | null } | null;
  parity?: StrategyRdParity | null;
  currency?: StrategyRdCurrencyInfo | null;
  /** Half / double cost, or assumed 0.04% + 2 ticks when the user set none. */
  cost_stress?: StrategyRdCostStress | null;
}

export interface StrategyRdAppliedLevels {
  entry: number;
  stop: number;
  target: number;
  direction: "long" | "short";
  risk: number;
  atr?: number | null;
  valid: boolean;
  invalid_reason?: string | null;
}

export interface StrategyRdForwardOutcome {
  result: StrategyRdForwardResult;
  bars_to_event?: number | null;
  /**
   * e.g. `gap_open`, `bar_magnifier`, `break_even`, `time_stop`, `partial`, `same_bar_both`
   * (several joined by `|`), or the skip reason.
   */
  note?: string | null;
  /** Actual exit (a gap fills at the bar's open) — not `levels.stop` / `levels.target`. */
  exit_price?: number | null;
  /** Only when the trade closed in parts; `exit_price` is their quantity-weighted average. */
  legs?: StrategyRdExitLeg[] | null;
  /** Only when break-even / the trail moved the stop: the initial stop, then each move. */
  stop_path?: StrategyRdStopStep[] | null;
}

export interface StrategyRdStopStep {
  /** Candles after the fill from which this stop is in force (0 = the initial stop). */
  bars: number;
  /** That candle's open time (ISO). */
  time?: string | null;
  price: number;
  why: "initial" | "break_even" | "trail" | string;
}

export interface StrategyRdExitLeg {
  /** Share of the original quantity, 0–1. */
  frac: number;
  price: number;
  kind: "partial" | "target" | "stop" | "exit" | string;
  bars?: number | null;
}

export type StrategyRdExcursionUnit =
  | "atr"
  | "r"
  | "pct_price"
  | "pips"
  | "ticks"
  | "price";

export interface StrategyRdExcursion {
  unit: StrategyRdExcursionUnit;
  mfe: number | null;
  mae: number | null;
  mfe_price?: number | null;
  mae_price?: number | null;
}

/** Position sizing for Apply / Confirm (Confirm v2). */
export type StrategyRdSizeMode = "risk_pct" | "fixed_qty";

/** How longs and shorts share the account (`sizing.position_mode`). */
export type StrategyRdPositionMode = "shared" | "independent" | "combined" | "one_at_a_time";

export interface StrategyRdSizing {
  mode: StrategyRdSizeMode;
  value: number;
  /** Account leverage. Omit for the asset default; Apply echoes the value used. */
  leverage?: number | null;
  /** Omitted = BE default `shared`. */
  position_mode?: StrategyRdPositionMode | null;
  /** % of each fill's value, charged on entry and exit (0–5). */
  commission_pct?: number | null;
  /** Ticks against the trade on market entries and stop exits (0–1000). */
  slippage_ticks?: number | null;
  /** Full spread as a price (e.g. 0.30 on gold); every fill pays half. Wins over `spread_pct`. */
  spread?: number | null;
  /** Full spread as % of price; every fill pays half (Lab candles are mid prices). */
  spread_pct?: number | null;
  /** Overnight swap, % of position value per night held through 17:00 New York (+ = you pay). */
  swap_long_pct?: number | null;
  swap_short_pct?: number | null;
  /** Apply echo: TradingView's tick for the symbol (slippage + stop/target rounding). */
  tick_size_used?: number | null;
  /** Daily limits on new trades (UTC day). Omit when both are blank. */
  limits?: StrategyRdAccountLimits | null;
}

export interface StrategyRdAccountLimits {
  max_trades_per_day?: number | null;
  max_losses_per_day?: number | null;
}

/**
 * Backend-decided caveat on a backtest (Apply `warnings[]`, also
 * `overview.warnings`). Texts are user-ready — render as sent; `code` is for icons.
 */
export interface StrategyRdBacktestWarning {
  code: string;
  severity: "warning" | "info" | string;
  title: string;
  message?: string | null;
  action?: {
    type: "check_robustness" | "open_settings" | string;
    label: string;
    /** For `open_settings`, e.g. `sizing.slippage_ticks`. */
    field?: string | null;
  } | null;
}

/** Closed-trade economics attached when Apply includes starting_equity + sizing. */
export interface StrategyRdFill {
  bar_time?: string | null;
  direction?: StrategyRdDirection | string | null;
  entry_time?: string | null;
  entry_price?: number | null;
  exit_time?: string | null;
  exit_price?: number | null;
  qty?: number | null;
  stop?: number | null;
  target?: number | null;
  result?: StrategyRdForwardResult | string | null;
  pnl?: number | null;
  r?: number | null;
  bars_to_event?: number | null;
  equity_after?: number | null;
  chart?: StrategyRdChartParams | null;
  /** Account leverage used. */
  leverage?: number | null;
  /** Entry price × qty, USD. */
  position_value?: number | null;
  /** Margin the trade holds, USD. */
  margin?: number | null;
  /** Position value ÷ equity at entry (e.g. 3.2). */
  leverage_used?: number | null;
  free_margin_at_entry?: number | null;
  /** Size was cut to fit the free margin. */
  size_capped?: boolean;
  /** Risk-% size before the cap. */
  size_wanted?: number | null;
  /** On `result: "skipped"`: insufficient_margin | opposite_position_open | position_open. */
  skipped_reason?: string | null;
  /** Charged on entry + exit; `pnl` is net of it. */
  commission?: number | null;
  entry_order?: "market" | "limit" | string | null;
  /** Partial closes TradingView's margin call would make. */
  margin_calls?: StrategyRdMarginCall[];
}

export interface StrategyRdMarginCall {
  bar_time?: string | null;
  qty?: number | null;
  price?: number | null;
  pnl?: number | null;
}

/** Apply `margin` block. */
export interface StrategyRdMarginSummary {
  leverage?: number | null;
  peak_margin?: number | null;
  min_free_margin?: number | null;
  margin_calls?: number | null;
}

/** Apply `currency` block. P&L is USD unless `converted` is false for a non-USD quote. */
export interface StrategyRdCurrencyInfo {
  account: string;
  converted: boolean;
  rate_pair?: string | null;
  reason?: string | null;
}

/** How closely this backtest's data matches TradingView's. */
export interface StrategyRdParity {
  level: "exchange" | "approximate" | string;
  tolerance_bars?: number | null;
  kind?: string | null;
  source?: string | null;
  tv_symbol?: string | null;
  reason?: string | null;
}

export interface StrategyRdEquityCurvePoint {
  t?: string | null;
  date?: string | null;
  pnl?: number | null;
  cumulative_pnl?: number | null;
  equity: number;
  trade_index?: number | null;
  bar_time?: string | null;
  result?: string | null;
  direction?: string | null;
}

export interface StrategyRdPeriodPnlBucket {
  date?: string | null;
  week_start?: string | null;
  pnl: number;
  trade_count?: number | null;
  wins?: number | null;
  losses?: number | null;
}

export interface StrategyRdClosedFill {
  bar_time?: string | null;
  t?: string | null;
  pnl?: number | null;
  result?: string | null;
  direction?: string | null;
  chart?: StrategyRdChartParams | null;
}

export interface StrategyRdApplyResult {
  bar_time: string;
  direction: StrategyRdDirection;
  pattern_id?: string;
  chart?: StrategyRdChartParams;
  levels: StrategyRdAppliedLevels;
  forward_outcome: StrategyRdForwardOutcome;
  excursion?: StrategyRdExcursion | null;
  fill?: StrategyRdFill | null;
}

export interface StrategyRdOutcomeSummary {
  target: number;
  stop: number;
  open: number;
  skipped: number;
  invalid?: number;
}

/** Top-level Apply overview (+ hypothesis.last_apply.overview). */
export interface StrategyRdApplyOverview {
  match_count: number | null;
  closed_count: number | null;
  open_count: number | null;
  skipped_count?: number | null;
  target_count: number | null;
  stop_count: number | null;
  win_rate: number | null;
  stop_rate?: number | null;
  open_rate?: number | null;
  avg_bars_to_event: number | null;
  median_bars_to_event?: number | null;
  min_bars_to_event: number | null;
  max_bars_to_event: number | null;
  avg_risk: number | null;
  median_risk?: number | null;
  avg_r?: number | null;
  sum_r: number | null;
  expectancy_r: number | null;
  profit_factor_r: number | null;
  avg_win_r?: number | null;
  avg_loss_r?: number | null;
  excursion_unit: StrategyRdExcursionUnit | null;
  avg_mfe: number | null;
  median_mfe?: number | null;
  min_mfe?: number | null;
  max_mfe?: number | null;
  avg_mae: number | null;
  median_mae?: number | null;
  min_mae?: number | null;
  max_mae?: number | null;
  /** Confirm v2 — present after sized Apply. */
  starting_equity?: number | null;
  sizing?: StrategyRdSizing | null;
  sum_pnl?: number | null;
  avg_pnl?: number | null;
  expectancy?: number | null;
  ending_equity?: number | null;
  profit_factor?: number | null;
  avg_win?: number | null;
  avg_loss?: number | null;
  largest_win?: number | null;
  largest_loss?: number | null;
  risk_reward_ratio?: number | null;
  return_on_starting_equity_pct?: number | null;
  max_drawdown?: number | null;
  max_drawdown_pct?: number | null;
  /** Peak-to-trough on cumulative R (negative = drawdown); present even without sizing. */
  max_drawdown_r?: number | null;
  max_win_streak?: number | null;
  max_loss_streak?: number | null;
  equity_curve?: StrategyRdEquityCurvePoint[] | null;
  daily_pnl?: StrategyRdPeriodPnlBucket[] | null;
  weekly_pnl?: StrategyRdPeriodPnlBucket[] | null;
  closed_fills?: StrategyRdClosedFill[] | null;
  /** Hold-out split of this backtest. */
  split?: StrategyRdHoldoutSplit | null;
  /** Halves, months, concentration on the locked trade list. */
  stability?: StrategyRdStability | null;
  /** No costs set: the same trades at the instrument's typical spread / swap. */
  with_realistic_costs?: { costs: Partial<StrategyRdSizing>; stats: StrategyRdHeadlineStats } | null;
  /** Years, top-1%, streaks, Monte Carlo, variants tried (`utils/strategyRdReport`). */
  report?: import("../utils/strategyRdReport").StrategyRdReport | null;
  /** Parameter search that produced this backtest (TES `sweep`). */
  sweep?: StrategyRdSweepTable | null;
  /** 1, or 2 for `independent` (then starting_equity is 2 × the input). */
  accounts?: number | null;
  /** Saved with the backtest so a reopened one shows them. */
  warnings?: StrategyRdBacktestWarning[] | null;
  /** Same shape per side — present when the recipe traded both sides. */
  by_side?: {
    long?: StrategyRdApplyOverview | null;
    short?: StrategyRdApplyOverview | null;
  } | null;
}

export interface StrategyRdLastApply {
  applied_at?: string | null;
  match_count?: number | null;
  starting_equity?: number | null;
  sizing?: StrategyRdSizing | null;
  outcome_summary?: StrategyRdOutcomeSummary | null;
  overview?: StrategyRdApplyOverview | null;
  results?: StrategyRdApplyResult[];
}

export interface StrategyRdApplyResponse {
  status: string;
  hypothesis_id: string;
  template: StrategyRdRuleTemplate;
  outcome_summary: StrategyRdOutcomeSummary;
  overview: StrategyRdApplyOverview | null;
  results: StrategyRdApplyResult[];
  report_strategy_hint?: StrategyRdHypothesis["report_strategy_hint"];
  starting_equity?: number | null;
  sizing?: StrategyRdSizing | null;
  /** Ties a robustness result to this backtest. */
  applied_at?: string | null;
  margin?: StrategyRdMarginSummary | null;
  currency?: StrategyRdCurrencyInfo | null;
  parity?: StrategyRdParity | null;
  warnings?: StrategyRdBacktestWarning[] | null;
}

export interface StrategyRdCreateHypothesisRequest {
  name?: string;
  scan_context: StrategyRdScanContext;
  template?: StrategyRdRuleTemplate;
  report_strategy_id?: string | null;
}

export interface StrategyRdApplyHypothesisRequest {
  matches: Array<{
    bar_time: string;
    direction: string;
    pattern_id?: string;
    chart?: StrategyRdChartParams;
  }>;
  /** Empty with `scan_job_id`: the server uses the scan's full window. */
  candles: StrategyRdOhlcBar[];
  scan_job_id?: string | null;
  template?: StrategyRdRuleTemplate | null;
  starting_equity: number;
  sizing: StrategyRdSizing;
}

export interface StrategyRdConfirmRequest {
  name?: string | null;
  set_as_current?: boolean;
}

export interface StrategyRdConfirmResponse {
  status: string;
  hypothesis_id: string;
  report_strategy_id: string;
  policy_version_id?: string | null;
  mapping_status?: string | null;
  name?: string | null;
  has_pine?: boolean;
  pine_parse_status?: string | null;
  /** Plain-language differences TradingView forces — show under the Pine code. */
  pine_notes?: string[];
  /** Chart the script must run on (finest TF in the recipe), e.g. "5m". */
  chart_timeframe?: string | null;
  has_research_sim?: boolean;
  research_sim_fill_count?: number | null;
  indicator_export_available?: boolean;
  position_mode?: StrategyRdPositionMode | string | null;
  /** One per TradingView script: `long` + `short`, or a single `both`. */
  pinescripts?: StrategyRdPineScript[];
  /** The automatic robustness test queued by Confirm. */
  robustness?: StrategyRdRobustness | null;
  confirmation?: {
    deep_link?: string | null;
    note?: string | null;
    overview?: StrategyRdApplyOverview | null;
    outcome_summary?: StrategyRdOutcomeSummary | null;
    validation?: string | null;
  } | null;
  report_strategy_hint?: StrategyRdHypothesis["report_strategy_hint"];
}

/**
 * Strategy-owned Research sim cohort (`GET …/research-sim`).
 * Lab canvas (strategy → cohort → report → optional account link) waits until
 * Confirm artifacts exist and report generate can consume research_sim — do not
 * overload journal workspace `"simulation"` nodes for this cohort.
 */
export interface StrategyRdPineScript {
  side: "long" | "short" | "both" | string;
  script: string;
}

export interface StrategyRdResearchSimPayload {
  schema?: string | null;
  hypothesis_id?: string | null;
  pattern_id?: string | null;
  scan_context?: StrategyRdScanContext | null;
  rule_template?: StrategyRdRuleTemplate | null;
  sizing?: StrategyRdSizing | null;
  starting_equity?: number | null;
  overview?: StrategyRdApplyOverview | null;
  fills?: StrategyRdFill[];
  confirmed_at?: string | null;
  /** Both scripts of a two-sided recipe (the strategy keeps the long one). */
  pinescript_by_side?: Record<string, string> | null;
}

export interface StrategyRdResearchSimResponse {
  status: string;
  strategy_id: string;
  has_research_sim: boolean;
  research_sim_fill_count: number;
  research_sim: StrategyRdResearchSimPayload;
}

export const STRATEGY_RD_CONFIRM_ERROR_SLUGS = [
  "confirm_requires_apply",
  "confirm_requires_sizing",
  "hypothesis_not_found",
  "confirm_failed",
] as const;

export type StrategyRdConfirmErrorSlug = (typeof STRATEGY_RD_CONFIRM_ERROR_SLUGS)[number];

export const DEFAULT_STRATEGY_RD_STARTING_EQUITY = 10000;
export const DEFAULT_STRATEGY_RD_SIZING: StrategyRdSizing = {
  mode: "risk_pct",
  value: 1,
};
